import { getConfiguration } from '@nuskin/configuration-sdk';
import {
    ConfigService,
    events,
    RunConfigService,
    storage,
    Base64,
    util,
    SponsorStorageService,
    nuskin,
    equinoxLocalStorage,
    UrlService
} from '@nuskin/ns-util';
import {UserService, AccountManager, SignupService} from './account.js';
import EquinoxService from './equinox-service.js';
import axios from 'axios';

const defaultClientId = 'default_client_id';
const defaultClientSecret = 'default_client_secret';

/**
 *
 * AuthenticationService - ns-account
 *
 * @namespace AuthenticationService
 */
let _config;
let ltoMode = false;
let samlMode = false;
let savedUsername = '';
let emailVerified = false;
let emailNeedsVerify = false;

let urlTemplates = {
    alternateLoginEmail: '/account-util/api/v1/webaccount/alternateLoginEmail?loginID={username}',
    guestSignup: '/account-service/api/v1/account',
    duplicateUserCheck: "/account-util/api/v1/account/username-ck?login=",
    emailValidate: '/validate-service/api/v1/email/address.json?address={email}'
};

let LEGACY_USER_ROLE = {
    PRIMARY: 'PRIMARY',
    ASSOCIATE: 'ASSOCIATE'
};
let USER_ROLE = {
    PRIMARY: 'Primary',
    ASSOCIATE: 'Associate',
    FAMILY_MEMBER: 'FamilyMember'
};
let LEGACY_USER_TYPE = {
    RETAIL_CUSTOMER: 'CUST',
    PREFERRED_CUSTOMER: 'PREF',
    PREFERRED_CUSTOMER_ALT: 'PREFERRED',
    PRIMARY_DISTRIBUTOR: 'DIST',
    ASSOCIATE: 'ASSOC',
    FAMILY_MEMBER: 'FAMILY'
};
let USER_TYPE = {
    RETAIL_CUSTOMER: 'Retail_Customer',
    PREFERRED_CUSTOMER: 'Preferred_Customer',
    PRIMARY_DISTRIBUTOR: 'Primary_Distributor',
    AGENT: 'AGENT',
    RETAILER: 'RETAILER',
    ASSOCIATE: 'Associate',
    FAMILY_MEMBER: 'FamilyMember'
};

let logout = function() {
    try {
        clearCookies();
    } catch (e) {
        console.log("Failed to clear cookies")
    }
}

let clearCookies = function() {
    try {
        // Clear out the adr override.
        sessionStorage.setItem('ADROverrideID', null);
        sessionStorage.setItem('orderReason', null);

        storage.removeItem(storage.metadata.NUSKIN_ACCOUNT_DATA);
        storage.removeItem(storage.metadata.USER_COOKIE_BASE);
        storage.removeItem(storage.metadata.LOGGEDIN_KEY,{ path: '/' });
        storage.removeItem(storage.metadata.MEC_JWT,{ path: '/' });
        storage.removeItem(storage.metadata.MEC_MIN,{ path: '/' });
        storage.removeItem(storage.metadata.MEC_MASTER,{ path: '/' });
        storage.removeItem(storage.metadata.EID, { path: '/' });
    } catch (e) {}
};

const oktaSignOut = url => {
    const isProd = RunConfigService.getEnvironmentCode() !== 'test';
    const logoutRedirectUrl = url ? url : '';
    const oktaSignOutBaseUrl = isProd ? 'id.nuskin.com/login/signout' : 'testid.nuskin.com/login/signout';
    const port = window.location.port ? `:${window.location.port}` : '';
    const urlPrefix = window.location.protocol + "//" + window.location.hostname + port;
    const oktaSignOutUrl = `${oktaSignOutBaseUrl}?fromURI=${urlPrefix}${logoutRedirectUrl}`;

    try {
        window.location.href = `https://${oktaSignOutUrl}`;
    } catch (err) {
        console.log(`Error signing out from Okta: ${err}`);
    }
}

let oktaLogin = async function (oktaData,username) {
    savedUsername = username;
    clearCookies();
    initRunConfig({loginConfig: {hasMarket:false}});

    let reqData = {};

    reqData.access_token = oktaData.accessToken;
    reqData.id_token = oktaData.idToken;

    let postUrl =  _config.awsUrl + '/authorize/v2';

    let axiosConfig = {
        headers : {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'client_id': _config.checkout.clientId || defaultClientSecret,
            'client_secret': _config.checkout.clientSecret || defaultClientSecret
        }
    };

    return axios.post(postUrl, reqData, axiosConfig)
        .then((awsResponse) => {
            if (!!awsResponse.data.eid && !!awsResponse.data.account.id) {
                if(typeof(awsResponse.data.person.personID) === 'undefined') {
                    awsResponse.data.person.personID = '';
                }
                googleAnalyticsEvent('awsSuccess','');
                try {
                    emailVerified = awsResponse.data.isVerified;
                    if(awsResponse.data.verifyCandidate) {
                        emailNeedsVerify = awsResponse.data.verifyCandidate;
                    }
                } catch (e) { }
                storeUserInfo(awsResponse.data);
                return "Login success";
            } else {
                googleAnalyticsEvent('awsFailure','no eid ' + reqData.UID);
                events.publish(events.authentication.INVALID_LOGIN_ATTEMPT, ['']);
                return "EID not found";
            }
        })
        .catch((error) => {
            googleAnalyticsEvent('awsFailure','Response Error' + reqData.UID);
            events.publish(events.authentication.INVALID_LOGIN_ATTEMPT, ['']);
            return error.response.data.messages[0].technicalMessage;
        });
};

let googleAnalyticsEvent = function(eventAction,eventMessage) {
    try {
        if (_config.googleAddressApiEnabled) {
            ga('send', 'event', {
                eventCategory: 'loginPage',
                eventAction: eventAction,
                eventLabel: eventMessage
            });
        }
    }catch(e) {}
};

let transformGuestSignupObj = (guestObj) => {
    if (guestObj && guestObj.firstName && guestObj.lastName && guestObj.email) {
        let runConfig = RunConfigService.getRunConfig(),
            config = ConfigService.getMarketConfig(),
            postObj = {
                SponsorID: SponsorStorageService.getSponsor(),
                CountryCode: runConfig.country,
                PreferredLang: runConfig.language,
                SignupChannel: SignupService.getSignupChannel({isGuest: !guestObj.password}),
                BEType: "CUST",
                AccountType: "INDIVIDUAL",
                Person: {
                    Name: [
                        {
                            Given: guestObj.firstName,
                            Family: guestObj.lastName,
                            Type: ""
                        }
                    ],
                    Contact: [
                        {
                            Country: runConfig.country,
                            Value: {
                                value: guestObj.email,
                                Type: "EMAIL"
                            }
                        }
                    ]
                }
            };
        if (guestObj.govtID && config.signup.governmentIDOptions) {
            postObj.Person.LegalItem = [
                {
                    type: config.signup.governmentIDOptions,
                    value: guestObj.govtID
                }
            ]
        }
        if (guestObj.password) {
            postObj.Login = {
                Password: guestObj.password,
                ID: guestObj.email
            };
        }
        if (guestObj.emailOptIn) {
            postObj.EmailOptIn = true;
        }
        return postObj;
    } else {
        console.error("Continue as guest failed: malformed guestObj firstName, lastName, email are required");
        return null;
    }
};


let isUsernameAvailable = function(username, callback) {
    const _config = ConfigService.getMarketConfig();
    let client_id = _config.checkout.clientId;
    let awsUrl = _config.awsUrl;

    const config = {
        method: 'get',
        url: `${awsUrl}/account-util-proxy/v1/account/username-ck?login=${username}`,
        headers: {
            client_id
        }
    };
    axios(config).then(response=>{
        callback(null, response)
    }).catch(err=>{
        callback(err, null)
    });
}

/**
 * Method for valiating the username exists in gigyaDump
 * @param {*} username
 * @param {*} callback
 */
let isUsernameAvailableInGigyaRef = function(username, callback) {
    const _config = ConfigService.getMarketConfig();
    let client_id = _config.checkout.clientId;
    let awsUrl = _config.awsUrl;

    const config = {
        method: 'get',
        url: `${awsUrl}/account-util-proxy/v1/account/gigyaCheck?username=${username}`,
        headers: {
            client_id
        }
    };
    axios(config).then(response=>{
        callback(null, response)
    }).catch(err=>{
        callback(err, null)
    });
}


/**
 * guestSignup Signup a guest under a sponsor so they get credit for the order.  This call will also login the guest user
 *
 * @param {Object} guestObj Information needed to create the guest account
 * @param {string} guestObj.firstName New user's first name
 * @param {string} guestObj.lastName New user's last name
 * @param {string} guestObj.email New user's email address
 * @param {string} [guestObj.govtID] optional only pass in if market requires it
 * @param {string} [guestObj.password] optional if password is passed in that means they chose to sign up and entered a password
 * @param {boolean} [guestObj.emailOptIn] optional if they choose to create an account then they can choose to opt in
 *
 * @returns {Promise<user>}
 */
let guestSignup = async (guestObj) => {
    let guestPostObj = transformGuestSignupObj(guestObj);
    if (guestPostObj) {
        if (guestPostObj.SponsorID) {
            let domain = RunConfigService.getRunConfig().baseUrl;
            let countryCd = RunConfigService.getRunConfig().country;
            let config = ConfigService.getMarketConfig();
            if (window.location.href.indexOf('localhost') !== -1) {
                domain = "https://test.nuskin.com"
            }
            const postUrl = domain + urlTemplates.guestSignup;

            const axiosConfig = {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'client_id': config.checkout.clientId || defaultClientId,
                    'client_secret': config.checkout.clientSecret || defaultClientSecret
                }
            };
            try {
                let response = await axios.post(postUrl, guestPostObj, axiosConfig);

                if (response && response.status === 200) {
                    if (response.data) {
                        let user = {
                            id: response.data.BECreateResponse.ID,
                            eid: response.headers.eid,
                            firstName: guestObj.firstName,
                            lastName: guestObj.lastName,
                            email: guestObj.email,
                            accountType: "20",
                            isGuest: !guestObj.password,
                            countryCd: countryCd
                        };
                        AccountManager.guestCheckoutLoginHandler(user);
                        return user;
                    }
                } else {
                    console.error(`Call to create guest failed: ${response.data}`);
                    return null;
                }
            } catch (err) {
                console.error(`Call to create guest failed:  ${err}`);
                return null;
            }
        } else {
            throw Error("No Sponsor");
        }
    } else {
        throw Error("Malformed guestObject missing data");
    }
};

/**
 * accountExists Returns true if an account with the email already exists
 * @param {string} email
 * @returns {Promise<boolean>}
 */
let accountExists = async (email) => {
    let exists = true;
    if (email) {
        let domain = RunConfigService.getRunConfig().baseUrl;
        let config = ConfigService.getMarketConfig();
        if (window.location.href.indexOf('localhost') !== -1) {
            domain = "https://test.nuskin.com";
        }
        const url = domain + urlTemplates.duplicateUserCheck + email;
        const axiosConfig = {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'client_id': config.checkout.clientId || defaultClientId,
                'client_secret': config.checkout.clientSecret || defaultClientSecret
            }
        };
        try {
            let response = await axios.get(url, axiosConfig);
            if (response && response.status === 200 && response.data && response.data.available) {
                exists = false;
            }
        } catch (err) {
            console.error(err);
        }
        console.log("returning exists" + exists);
        return exists;
    }
};


let setAccountType = function(custType) {
    if (custType == 'DIST' || custType == 'DISTRIBUTOR') {
        return '10';
    } else if (custType == 'CUST' || custType == 'CUSTOMER') {
        return '20';
    } else if (custType == 'PREF' || custType == 'PREFERREDCUST' || custType == 'PREFERRED') {
        return '30';
    } else {
        return '0';
    }
};

let getCustomerType = function(custType) {
    if (custType == 'DIST') {
        return 'DISTRIBUTOR';
    } else if (custType == 'CUST') {
        return 'CUSTOMER';
    } else if (custType == 'PREF' || custType == 'PREFERRED') {
        return 'PREFERREDCUST';
    } else {
        return '';
    }
};


function accountInfoWrapper() {

    return new Promise((resolve, reject) => {
        getLoginInfo((response) => {
            let profileUrl = '';
            if (response.errorCode == 0) {
                let profile = response['profile'];
                profileUrl =  profile.photoURL;
            }
            resolve(profileUrl);
        });
    })
}

function getPriceType(accountType) {
    let priceType = 'WHL';
    try {
        priceType = accountType == '20' ? 'RTL' : 'WHL';
    } catch(e) {}
    return priceType;
}

async function getAccountInfo() {
    let result = '';
    try {
        result = await accountInfoWrapper();
    } catch (err) {}
    return result;
}

async function storeUserInfo (userData) {
    let loginTime = new Date().getTime();
    let resultObject = {
        'id': userData.account.id,
        'name': userData.person.localName,
        'localName': userData.person.localName,
        'email': userData.person.email,
        'accountType': setAccountType(userData.account.customerType),
        'countryCd': userData.account.country,
        'priceType': getPriceType(setAccountType(userData.account.customerType)),
        'customerType': getCustomerType(userData.account.customerType),
        'eid': userData.eid,
        'lastName': userData.person.lastName,
        'firstName': userData.person.firstName,
        'loginTime': loginTime,
        'encryptedId': userData.eid,
        'personId':userData.person.personID,
        'federatedId':userData.person.gigyaUID,
        'noFalloutMsg':userData.account.noFalloutMsg,
        'isVolumesLoaded': false,
        'emailVerified': emailVerified,
        'emailNeedsVerify':emailNeedsVerify
    };
    if (0 < savedUsername.length) {
        resultObject.username = savedUsername;
    }

    const marketLocale = UrlService.getLocale();
    const [, market] = marketLocale.split("_");
    const config = (await getConfiguration(["Equinox_Markets"])).Equinox_Markets;

    if (config.country_code === market && config.active && equinoxLocalStorage.isEquinoxEnabled()) {
        resultObject = await EquinoxService.equinoxSessionDetailMapper(resultObject)

        resultObject.accountType = setAccountType(resultObject.accountType);
        resultObject.priceType =  getPriceType(resultObject.accountType);
        resultObject.customerType = getCustomerType(resultObject.accountType)

        await EquinoxService.getOrCreateFavorites();
    }

    localStorage.setItem('nuskin.account.data', JSON.stringify(resultObject));
    //   resetOPSCookies();
    UserService.setUser(resultObject);
    UserService.setLoggedIn(true);
    // Transform for MEC cookies
    let min = transformMinInfo(userData);
    let master = transformPersonInfo(userData);

    let eidDate = new Date();
    eidDate.setTime(eidDate.getTime() + (20 * 60 * 1000));
    storage(storage.metadata.EID,userData.eid,{ domain: '.nuskin.com', path: '/', expires: eidDate });
    storage(storage.metadata.MEC_JWT,userData.eid,{ path: '/' });
    storage(storage.metadata.MEC_MIN,min,{ path: '/' });
    storage(storage.metadata.MEC_MASTER,master,{ path: '/' });
    events.publish(events.authentication.VALID, [UserService.getUser()]);
    try {
        if (userData.account.temporary) {
            events.publish(events.authentication.PNP_CHECK, []);
        } else {
            if(samlMode) {
                gigya.fidm.saml.continueSSO();
            } else if(ltoMode) {
                events.publish(events.authentication.LTO_QUALIFICATIONS, []);
            } else {
                events.publish(events.authentication.LOGIN_COMPLETE, [UserService.getUser()]);
            }
        }
    } catch (e) {
        events.publish(events.authentication.LOGIN_COMPLETE, [UserService.getUser()]);
    }
}

let isLoggedIn = function() {
    if(storage.getItem(storage.metadata.LOGGEDIN_KEY)) {
        return true;
    } else {
        return false;
    }
};


let transformPersonInfo = function(awsData) { // This data could be gotten from the authorize call.
    let userData = {
        'accountId': awsData.account.id,
        'accountType': 'Individual',
        'emailId': awsData.person.email,
        'emailVerified': false,
        'gigyaId': awsData.person.gigyaUID,
        'userId': awsData.person.personID.toLowerCase(),
        'userRole': 'Primary',
        'userType': 'Primary_Distributor',
        'username': ''
    };

    // userRole
    if (awsData.person.type == LEGACY_USER_ROLE.ASSOCIATE) {
        userData.userRole = USER_ROLE.ASSOCIATE;
    } else if (awsData.person.type == LEGACY_USER_ROLE.PRIMARY) {
        userData.userRole = USER_ROLE.PRIMARY;
    }

    // userType
    if (awsData.account.customerType == LEGACY_USER_TYPE.PRIMARY_DISTRIBUTOR) {
        if (awsData.person.type == LEGACY_USER_ROLE.ASSOCIATE) {
            userData.userType = USER_TYPE.ASSOCIATE;
        } else {
            userData.userType = USER_TYPE.PRIMARY_DISTRIBUTOR;
        }
    } else if (awsData.account.customerType == LEGACY_USER_TYPE.PREFERRED_CUSTOMER
        || awsData.account.customerType == LEGACY_USER_TYPE.PREFERRED_CUSTOMER_ALT) {
        userData.userType = USER_TYPE.PREFERRED_CUSTOMER;
    } else if (awsData.account.customerType == LEGACY_USER_TYPE.RETAIL_CUSTOMER) {
        userData.userType = USER_TYPE.RETAIL_CUSTOMER;
    }
    if (0 < savedUsername.length) {
        userData.username = savedUsername;
    }

    return userData;
};


let transformMinInfo = function (awsData) {
    let minData = {};

    minData.profileCommPreferences = {
        'gender': '',
        'languagePreferences': awsData.account.preferredLanguage,
        'marketingEmail': true
    };
    minData.profileContactInfo = {
        'email': awsData.person.email
    };
    minData.profileHeader = {
        'accountId': awsData.account.id,
        'firstName': awsData.person.firstName,
        'lastName': awsData.person.lastName,
        'profileThumbnailUrl': '',
        'preferredName': awsData.account.preferredName,
        'userId': awsData.person.personID
    };
    minData.profilePersonalInfo = {
        'gender': '',
        'personalId': awsData.person.personID
    };
    minData.profileSponsorDetails = {
        'id': awsData.account.sponsorId,
        'country': '',
        'names': {
            'default': ''
        }
    };


    minData.profilePrivacyOptionInfo = {
        'acceptMarketingEmail': true,
        'acceptMarketingSMS': false,
        'contactPreference': 'EMAIL',
        'showContactInfo': false,
        'showContactInfoToFamilyAndFriends': false,
        'showContactInfoToFirstUpLine': false,
        'visibleInScannerLocator': false,
        'visibleInSponsorSearch': false
    };
    return minData;
};

let isGigyaReady = function() {
    let gigyaReady = false;
    try {
        gigyaReady = gigya.isReady;
    } catch(e){}
    return gigyaReady;
};

let makeAjaxCall = function (ajaxType, url, data, callbacks) {
    $.ajax({
        type: ajaxType,
        url: url,
        contentType: "application/json",
        dataType: 'json',
        data: JSON.stringify(data),
        headers: {
            client_id: _config.checkout.clientId || defaultClientId,
            client_secret: _config.checkout.clientSecret || defaultClientSecret,
            Accept: 'application/json'
        },
        timeout: callbacks.timeout,
        success: callbacks.success,
        error: callbacks.error
    });
}

let forgotPasswordAjaxCallbacks = {
    timeout: function (response) {
        events.publish(events.authentication.FORGOT_PASSWORD_ERROR, ['']);
    },
    success: function (response) {
        events.publish(events.authentication.FORGOT_PASSWORD_SENT, ['']);
    },
    error: function (response) {
        events.publish(events.authentication.FORGOT_PASSWORD_ERROR, ['']);
    }
}


let forgotPasswordJITProxy = function (baseUrl, loginID, forgotPasswordConfig) {
    let ajaxType = "PUT";
    let resetPasswordUrl = `${baseUrl}/account-util-proxy/v1/forgot-password`
    let data = {
        templateName: "GigyaPWChange",
        templateData: {
            DESTINATION_URL: forgotPasswordConfig.destinationUrl,
            CANCEL_URL: forgotPasswordConfig.cancelUrl,
            LOCALE_PAGE: forgotPasswordConfig.localepage,
            MARKET_PAGE: forgotPasswordConfig.market
        },
        loginID: loginID,
        eventType: 'FORGOT_PASSWORD',
        eventSource: 'LOGIN_VUE_COMPONENT'
    };
    if (loginID) {
        makeAjaxCall(ajaxType, resetPasswordUrl, data, forgotPasswordAjaxCallbacks)
    }
};

let generateLoginToken = function (username) {
    let resetPasswordUrl = urlTemplates.alternateLoginEmail.replace('{username}', username);
    if (typeof _config === 'undefined') {
        _config = ConfigService.getMarketConfig();
    }
    const axiosConfig = {
        headers: {
            'client_id': _config.checkout.clientId || defaultClientId,
            'client_secret': _config.checkout.clientSecret || defaultClientSecret
        }
    };
    try {
        axios.get(resetPasswordUrl, axiosConfig);
    } catch (err) {}
};

let resetPasswordAccountUtilProxy = async (resetToken,password) => {
    if (typeof _config === 'undefined') {
        _config = ConfigService.getMarketConfig();
    }
    let resetPasswordurl = _config.awsUrl + '/account-util-proxy/v1/password-reset';

    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': _config.checkout.clientId || defaultClientSecret,
        'client_secret': _config.checkout.clientSecret || defaultClientSecret
    }
    axios.request({
        method: 'put',
        url: resetPasswordurl,
        headers: headers,
        responseType: 'json',
        data: {
            passwordResetToken: resetToken,
            newPassword: password
        }
    }).then((awsResponse) => {
        if (!!awsResponse && !!awsResponse.data) {
            /**
             * N001 -> Nuskin custom error code
             */
            if (awsResponse.data.errorCode !== 0 && awsResponse.data.errorCode === 'N001') {
                events.publish(events.authentication.RESET_PASSWORD_EXPIRED, ['']);
            } else if (awsResponse.data.errorCode !== 0) {
                events.publish(events.authentication.RESET_PASSWORD_ERROR, ['']);
            } else {
                events.publish(events.authentication.RESET_PASSWORD_SUCCESS, [''])
            }
        }
    }).catch(() => {
        events.publish(events.authentication.RESET_PASSWORD_ERROR, ['']);
    });
}


let initRunConfig = function(loginConfig) {
    if (typeof _config === 'undefined') {
        try {
            if (loginConfig.isLTO) {
                ltoMode = true;
            }
            if (loginConfig.isSAML) {
                samlMode = true;
            }
        }catch (e) {}
        if (loginConfig.hasMarket) {
            let loginRunConfig = {};

            loginRunConfig.country = loginConfig.market;
            loginRunConfig.language = loginConfig.language;
            loginRunConfig.siteConfigurationPath = loginConfig.configurationPath;
            let url = window.location.href;
            let arr = url.split("/");
            loginRunConfig.baseUrl = arr[0] + "//" + arr[2];
            RunConfigService.setRunConfig(loginRunConfig);
        }
        _config = ConfigService.getMarketConfig();
    }
};


let getMecCustomerType = function(userObject) {
    if (userObject.isDistributor()) {
        return 'DISTRIBUTOR';
    } else if (userObject.isCustomer()) {
        return 'CUSTOMER';
    } else if (userObject.isPreferredCustomer()) {
        return 'PREFERREDCUST';
    } else {
        return '';
    }
};

let transformUserObjToAWSData = function(userObject,personId) {
    let awsData = {
        "account":{
            "id":userObject.id,
            "sponsorId":'',
            "country":userObject.countryCd,
            "currency":"USD",
            "preferredLanguage":"ENG",
            "customerType":getMecCustomerType(userObject),
            "temporary":false,
            "deleted":false,
            "priceType":userObject.priceType,
            "preferredName":userObject.displayName,
            "contacts":[
                {
                    "type":"EMAIL",
                    "country":userObject.countryCd
                }
            ]
        },
        "person":{
            "sapID":userObject.id,
            "personID":personId,
            "gigyaUID":'',
            "firstName":userObject.firstName,
            "lastName":userObject.lastName,
            "localName":userObject.localName,
            "type":"PRIMARY",
            "accountDeleted":false
        },
        "eid":userObject.eid,
        "status":"200",
        "messages":[

        ]
    };
    return awsData;
};

let  getPersonInfo = function(userObject) {
    if (typeof _config === 'undefined') {
        _config = ConfigService.getMarketConfig();
    }
    return new Promise(function(resolve, reject) {

        let getUrl = _config.awsUrl + '/customer/v1/account/' + userObject.id;

        let axiosConfig = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'client_id': _config.checkout.clientId || defaultClientSecret,
            'client_secret': _config.checkout.clientSecret || defaultClientSecret,
            'Authorization': 'JWT ' + userObject.eid
        };

        try {
            axios.request({
                method: 'get',
                url: getUrl,
                headers: axiosConfig,
                responseType: 'json'
            }).then(function (awsResponse) {
                let result = {
                    sponsorId: '',
                    personId: '',
                    federatedId: ''
                };
                try {
                    if (!!awsResponse.data.account && !!awsResponse.data.account.persons) {
                        result.personId = awsResponse.data.account.persons[0].personId;
                        result.sponsorId = awsResponse.data.account.sponsorId;
                        result.federatedId = awsResponse.data.account.persons[0].federatedId;
                    }

                } catch (err) {
                }
                resolve(result);
            }).catch(function (error) {
                // handle error
                let result = {
                    sponsorId: '',
                    personId: '',
                    federatedId: ''
                };
                resolve(result);
            })
        } catch (e) {
            console.error("getPersonInfo exception: " , e);
        }
    })
};

let authWithEid = async function(eid) {
    clearCookies();
    const globalAuthURL = util.getValue("nuskin.configuration.globalAuthEIDUrl", "https://www.nuskin.com/simpleBe/api/eid/be") + "?eid=" + eid;
    const nsConfig = ConfigService.getMarketConfig();

    const ajaxPromise = new Promise((resolve, reject) => $.ajax({
        url: globalAuthURL,
        type: "GET",
        beforeSend: function(xhr) {
            xhr.setRequestHeader("client_id", nsConfig.checkout.clientId || defaultClientId);
            xhr.setRequestHeader("client_secret", nsConfig.checkout.clientSecret || defaultClientSecret);
        }
    }).done(resolve).fail(reject));

    let data;
    try {
        data = await ajaxPromise;
    } catch (ajaxErr) {
        const error = JSON.parse(ajaxErr.responseText);
        events.publish(events.authentication.INVALID, error);
        throw error;
    }

    let resultObject = updateResponseObject(data[nuskin.account.GLOBAL_AUTH_RETURN_OBJECT]);
    // Set login time.
    resultObject.loginTime = new Date().getTime();
    UserService.setUser(resultObject);
    UserService.setLoggedIn(true);
    events.publish(events.authentication.VALID, [resultObject]);
    produceMecCookies();
    return resultObject;
};

let updateResponseObject = function(resultObject) {
    resultObject.eid = resultObject.encryptedId;
    resultObject.lastName = resultObject.name.substring(0,resultObject.name.indexOf(',')).trim();
    resultObject.firstName = resultObject.name.substring(resultObject.name.indexOf(',') + 1).trim();
    resultObject.accountType = resultObject.accountType.toString();
    return resultObject;
};

let produceMecCookies = function() {
    let personId = '';
    try {
        personId = JSON.parse('{' + Base64.decode(UserService.getUser().eid).split("{")[2].split("}")[0] + '}')['ns-uid'];
    } catch(e) {}
    try {
        let userObj = UserService.getUser();
        userObj.personId = personId;
        let mecUserData = transformUserObjToAWSData(userObj, personId);
        let min = transformMinInfo(mecUserData);
        let master = transformPersonInfo(mecUserData);
        storage(storage.metadata.MEC_JWT, mecUserData.eid, {path: '/'});
        storage(storage.metadata.MEC_MIN, min, {path: '/'});
        storage(storage.metadata.MEC_MASTER, master, {path: '/'});
    } catch(e) {}
};

/**
 * Is market equinox enabled?
 * @returns {boolean}
 */
const isMarketEquinoxEnabled  = async function() {
    const marketLocale = UrlService.getLocale();
    const [, market] = marketLocale.split("_");
    const config = (await getConfiguration(["Equinox_Markets"])).Equinox_Markets;
    return (config.country_code === market && config.active && equinoxLocalStorage.isEquinoxEnabled()) ;
}


/**
 * Logout equinox customer
 * @returns {boolean}
 */
const equinoxLogout = async function () {
    try {
        if (!(isMarketEquinoxEnabled())) {
            throw Error(`Market is not equinox enabled.`);
        }

        await EquinoxService.equinoxCustomerLogout();

    } catch (error) {
        console.log(`Error: `, error);
        return false;
    }

    return true;
};

export default {
    oktaLogin,
    oktaSignOut,
    isUsernameAvailable,
    isLoggedIn,
    logout,
    resetPasswordAccountUtilProxy,
    forgotPasswordJITProxy,
    initRunConfig,
    authenticateWithEid: authWithEid,
    generateMecCookies: produceMecCookies,
    isLoginAllowed: function(id, allowedPrefixesList) {
        let goodToLogin = true;
        if (allowedPrefixesList) {
            goodToLogin = false;

            if (id && id.length > 2) {
                if (_.includes(allowedPrefixesList, id.substring(0, 2))) {
                    goodToLogin = true;
                }
            }
        }
        return goodToLogin;
    },
    guestSignup,
    accountExists,
    generateLoginToken,
    isUsernameAvailableInGigyaRef,
    equinoxLogout,
    isMarketEquinoxEnabled
};
